import React, { useState } from 'react';
import { 
  Lock, 
  Eye, 
  EyeOff, 
  Shield,
  Key
} from 'lucide-react';
import { useCMSAuth } from '../../store/cmsStore';

interface AdminLoginProps {
  onLogin: () => void;
}

export default function AdminLogin({ onLogin }: AdminLoginProps) {
  const { login, setPassword, hasPassword } = useCMSAuth();
  const [password, setPasswordInput] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSettingPassword, setIsSettingPassword] = useState(!hasPassword());

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (isSettingPassword) {
      // Setting new password
      if (password.length < 4) {
        setError('كلمة المرور يجب أن تكون 4 أحرف على الأقل');
        return;
      }
      if (password !== confirmPassword) {
        setError('كلمتا المرور غير متطابقتين');
        return;
      }
      setPassword(password);
      login(password);
      onLogin();
    } else {
      // Logging in
      if (login(password)) {
        onLogin();
      } else {
        setError('كلمة المرور غير صحيحة');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-brown-50 to-gold-50 flex items-center justify-center p-4" dir="rtl">
      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-brown-600 rounded-2xl mb-4">
            <Shield className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 font-amiri">لوحة التحكم</h1>
          <p className="text-gray-600 font-cairo mt-2">سدرة المنتهى — إدارة المحتوى</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <Key className="w-5 h-5 text-brown-600" />
            <h2 className="text-lg font-bold text-gray-900 font-cairo">
              {isSettingPassword ? '設定 كلمة مرور جديدة' : 'تسجيل الدخول'}
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 font-cairo mb-2">
                {isSettingPassword ? 'كلمة المرور الجديدة' : 'كلمة المرور'}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPasswordInput(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 pl-12 focus:ring-2 focus:ring-brown-500 focus:border-transparent font-cairo"
                  placeholder="أدخل كلمة المرور"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Confirm Password (only when setting new password) */}
            {isSettingPassword && (
              <div>
                <label className="block text-sm font-medium text-gray-700 font-cairo mb-2">
                  تأكيد كلمة المرور
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-brown-500 focus:border-transparent font-cairo"
                  placeholder="أعد إدخال كلمة المرور"
                />
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm font-cairo">
                {error}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full bg-brown-600 text-white py-3 rounded-lg font-cairo font-bold text-lg hover:bg-brown-700 transition"
            >
              {isSettingPassword ? 'تعيين كلمة المرور' : 'دخول'}
            </button>
          </form>

          {/* Help Text */}
          <p className="text-center text-gray-500 text-sm font-cairo mt-6">
            {isSettingPassword
              ? 'ستُستخدم هذه كلمة المرور للدخول إلى لوحة التحكم'
              : 'أدخل كلمة المرور للوصول إلى لوحة التحكم'}
          </p>
        </div>

        {/* Footer */}
        <p className="text-center text-gray-400 text-xs font-cairo mt-6">
          © {new Date().getFullYear()} سدرة المنتهى — جميع الحقوق محفوظة
        </p>
      </div>
    </div>
  );
}